import { parseDbTimestamp, pickT } from "@repo/shared";
import type { LocaleCode } from "@repo/shared/types";
import { AlertCircle, CheckCircle2, FileSignature, Loader2, Shield } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { ThemeToggle } from "@/components/theme-toggle";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError, apiErrorMessage } from "@/lib/api/_client";
import { languageFromError } from "@/lib/api/public-flow/contact-info";
import { getSignatureStatus, getSigningUrl } from "@/lib/api/public-flow/signatures";
import { useSession } from "@/lib/auth-client";
import { useUserLanguage } from "@/lib/i18n/context";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./page.i18n";

export default function PublicSigningPage() {
  const { formatDateTime } = useRegionalFormat();
  // QL-17 issue 027 — the flow's language, from the signing-url response or a
  // refused link's error body. Until it is known (and for an advisor) the
  // viewer's own.
  const { userLanguage } = useUserLanguage();
  const [flowLanguage, setFlowLanguage] = useState<LocaleCode | null>(null);
  const t = pickT(T, flowLanguage ?? userLanguage);

  const params = useParams();
  const signToken = params.token as string;

  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signingUrl, setSigningUrl] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string>("");
  const [flowName, setFlowName] = useState<string>("");
  const [status, setStatus] = useState<string | null>(null);
  const [signedAt, setSignedAt] = useState<string | null>(null);
  const [signerName, setSignerName] = useState<string | null>(null);

  // Better Auth advisor session, shared app-wide via the session atom
  const { data: advisorSession, isPending: advisorSessionPending } = useSession();
  const isAdvisor = !!advisorSession?.user;

  useEffect(() => {
    // Wait until the advisor session check has settled
    if (advisorSessionPending) return;

    if (isAdvisor) {
      // Advisor is logged in - show error
      setError(t.advisorCannotAccess);
      setLoading(false);
      return;
    }

    // Not logged in as advisor - proceed to load signing URL
    loadSigningUrl();

    async function loadSigningUrl() {
      try {
        setLoading(true);
        setError(null);

        const data = await getSigningUrl(signToken);

        setFlowLanguage(data.data.language);
        setSigningUrl(data.data.signingUrl);
        setClientName(data.data.clientName);
        setFlowName(data.data.flowName);
      } catch (err) {
        const language = languageFromError(err);
        setFlowLanguage(language);
        const errT = pickT(T, language ?? userLanguage);
        if (err instanceof ApiError) {
          setError(apiErrorMessage(err, errT.invalidOrExpiredLink));
        } else {
          setError(errT.failedToLoadSignaturePage);
        }
      } finally {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signToken, advisorSessionPending, isAdvisor]);

  const handleStartSigning = () => {
    if (signingUrl) {
      // /p/signature-complete reads these after Idura sends the signer back (QL-720)
      try {
        if (clientName) sessionStorage.setItem("signatureClientName", clientName);
        if (flowLanguage) sessionStorage.setItem("signatureFlowLanguage", flowLanguage);
      } catch {
        // storage blocked: the completion page just omits the name
      }
      // Redirect to Criipto signing page
      window.location.href = signingUrl;
    }
  };

  const handleCheckStatus = async () => {
    setChecking(true);
    setError(null);

    try {
      const data = await getSignatureStatus(signToken);

      setStatus(data.data.status);
      setSignedAt(data.data.signedAt || null);
      setSignerName(data.data.signerName || null);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(apiErrorMessage(err, t.failedToCheckStatus));
      } else {
        setError(t.failedToCheckSignatureStatus);
      }
    } finally {
      setChecking(false);
    }
  };

  const getStatusBadge = (statusValue: string) => {
    switch (statusValue) {
      case "signed":
        return <Badge variant="success">{t.signed}</Badge>;
      case "signing":
        return <Badge variant="secondary">{t.inProgress}</Badge>;
      case "pending":
        return <Badge variant="secondary">{t.pending}</Badge>;
      case "expired":
        return <Badge variant="destructive">{t.expired}</Badge>;
      case "cancelled":
        return <Badge variant="destructive">{t.cancelled}</Badge>;
      case "failed":
        return <Badge variant="destructive">{t.failed}</Badge>;
      default:
        return <Badge variant="secondary">{statusValue}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !signingUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-6 w-6" />
              <CardTitle>{t.unableToLoadSignaturePage}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error || t.invalidOrExpiredLink}</AlertDescription>
            </Alert>
            <p className="text-sm text-muted-foreground mt-4">{t.contactAdvisorForAssistance}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <FileSignature className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">{t.documentSigning}</h1>
                <p className="text-sm text-muted-foreground">{flowName}</p>
              </div>
            </div>
            <ThemeToggle />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>
              {t.welcome} {clientName}
            </CardTitle>
            <CardDescription>{t.requestedToSign}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Information */}
            <div className="space-y-4">
              <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-primary mt-0.5" />
                  <div className="space-y-2">
                    <h3 className="font-semibold text-primary">{t.secureDigitalSigning}</h3>
                    <p className="text-sm text-primary/80">{t.secureDigitalSigningDesc}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="font-medium">{t.whatHappensNext}</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                  <li>{t.step1}</li>
                  <li>{t.step2}</li>
                  <li>{t.step3}</li>
                  <li>{t.step4}</li>
                  <li>{t.step5}</li>
                  <li>{t.step6}</li>
                </ol>
              </div>
            </div>

            {/* Status Display */}
            {status && (
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{t.currentStatus}</span>
                  {getStatusBadge(status)}
                </div>
                {status === "signed" && signedAt && (
                  <div className="text-sm text-muted-foreground">
                    <p>
                      {t.signedLabel} {formatDateTime(parseDbTimestamp(signedAt))}
                    </p>
                    {signerName && (
                      <p>
                        {t.signerLabel} {signerName}
                      </p>
                    )}
                  </div>
                )}
                {status === "signed" && (
                  <Alert variant="success" className="mt-2">
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription>{t.signatureSuccessfullyCompleted}</AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="pt-4 flex flex-col sm:flex-row gap-2">
              <Button
                size="lg"
                onClick={handleStartSigning}
                className="flex-1 gap-2 min-h-9"
                disabled={status === "signed"}
              >
                {status === "signed" ? t.alreadySigned : t.startSigningProcess}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={handleCheckStatus}
                className="gap-2"
                loading={checking}
              >
                {t.checkStatus}
              </Button>
            </div>

            {/* Security Notice */}
            <div className="pt-4 border-t">
              <div className="flex items-start gap-3 text-sm">
                <CheckCircle2 className="h-4 w-4 text-success mt-0.5" />
                <div className="text-muted-foreground">
                  <p className="font-medium text-foreground">{t.secureAndCompliant}</p>
                  <p>{t.secureAndCompliantDesc}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Help Section */}
        <div className="mt-6 space-y-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium text-sm mb-2">{t.afterSigning}</h4>
            <p className="text-sm text-muted-foreground">{t.afterSigningDesc}</p>
          </div>

          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium text-sm mb-2">{t.needHelp}</h4>
            <p className="text-sm text-muted-foreground">{t.needHelpDesc}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
